import type { TemplateDto } from "@mandoob/shared";

/**
 * The Meta templates use named body params following an "{ar|en}_{field}"
 * convention ({{ar_name}}, {{en_driver_phone}}, ...) — each body repeats the
 * same shipment data in Arabic then English. The synced variables carry those
 * param names, so which fields a template needs is detected from them directly;
 * no per-template mapping. A template with any variable outside the convention
 * falls back to generic per-variable inputs.
 */

/** Data a shipment can feed into a template body. */
export interface ShipmentTemplateData {
  clientName: string;
  trackingNumber: string;
  company: string;
  /** Display-formatted driver calling number. */
  driverPhone: string;
  driverName: string;
}

export type FieldKey = keyof ShipmentTemplateData;

/** Locale keys naming each shipment field (tags, preview placeholders). */
export const FIELD_LABEL_KEYS: Record<FieldKey, string> = {
  clientName: "Mandoob:NewDelivery.ClientName",
  trackingNumber: "Mandoob:Delivery.TrackingNumber",
  company: "Mandoob:Delivery.Company",
  driverPhone: "Mandoob:Delivery.CallingPhone",
  driverName: "Mandoob:Delivery.DriverName",
};

/** param_name field part → shipment field. */
const FIELD_BY_PARAM: Record<string, FieldKey> = {
  name: "clientName",
  tracking: "trackingNumber",
  company: "company",
  driver_phone: "driverPhone",
  driver_name: "driverName",
};

export const CLIENT_NAME_FALLBACK_AR = "المستلم";
export const CLIENT_NAME_FALLBACK_EN = "Recipient";

interface Slot {
  lang: "ar" | "en";
  field: FieldKey;
}

function parseSlot(variable: string): Slot | null {
  const match = /^(ar|en)_(.+)$/.exec(variable);
  const field = match && FIELD_BY_PARAM[match[2]];
  return field ? { lang: match[1] as Slot["lang"], field } : null;
}

function slotsFor(template: TemplateDto): Slot[] | null {
  if (template.variables.length === 0) return null;
  const slots = template.variables.map(parseSlot);
  return slots.every((slot) => slot !== null) ? (slots as Slot[]) : null;
}

/** Which editable shipment fields the template's body actually uses. */
export function templateNeeds(template: TemplateDto): {
  mapped: boolean;
  company: boolean;
  driverPhone: boolean;
  driverName: boolean;
} {
  const slots = slotsFor(template);
  return {
    mapped: !!slots,
    company: !!slots?.some((slot) => slot.field === "company"),
    driverPhone: !!slots?.some((slot) => slot.field === "driverPhone"),
    driverName: !!slots?.some((slot) => slot.field === "driverName"),
  };
}

function slotValue({ lang, field }: Slot, data: ShipmentTemplateData): string {
  const value = data[field].trim();
  if (field === "clientName" && !value) {
    return lang === "ar" ? CLIENT_NAME_FALLBACK_AR : CLIENT_NAME_FALLBACK_EN;
  }
  return value;
}

/** Named variables for a mapped template; null for unmapped ones. */
export function buildTemplateVariables(
  template: TemplateDto,
  data: ShipmentTemplateData,
): Record<string, string> | null {
  const slots = slotsFor(template);
  if (!slots) return null;
  const values: Record<string, string> = {};
  template.variables.forEach((name, index) => {
    values[name] = slotValue(slots[index], data);
  });
  return values;
}

/** Unique shipment fields the template uses, in body order ([] if unmapped). */
export function templateFields(template: TemplateDto): FieldKey[] {
  const slots = slotsFor(template);
  if (!slots) return [];
  const fields: FieldKey[] = [];
  for (const slot of slots) {
    if (!fields.includes(slot.field)) fields.push(slot.field);
  }
  return fields;
}

/**
 * Groups a template's variables for mapping UIs: the {ar|en}_{field} pairs
 * collapse into ONE group per shipment field (a single input feeds both
 * languages), labelled like the template-preview tags; variables outside the
 * convention stay as their own single-name groups.
 */
export function groupTemplateVariables(
  variables: string[],
): { key: string; field: FieldKey | null; names: string[] }[] {
  const groups: { key: string; field: FieldKey | null; names: string[] }[] = [];
  for (const name of variables) {
    const slot = parseSlot(name);
    const key = slot ? slot.field : name;
    const existing = groups.find((group) => group.key === key);
    if (existing) existing.names.push(name);
    else groups.push({ key, field: slot?.field ?? null, names: [name] });
  }
  return groups;
}

/**
 * Body text with variables filled for previews: the same values the real send
 * would use, with a «labelled» placeholder for fields that are still empty.
 * Unmapped templates come back untouched.
 */
export function fillTemplateBody(
  template: TemplateDto,
  data: ShipmentTemplateData,
  label: (field: FieldKey) => string,
): string {
  const slots = slotsFor(template);
  if (!slots) return template.bodyText;
  let body = template.bodyText;
  template.variables.forEach((name, index) => {
    const value =
      slotValue(slots[index], data) || `«${label(slots[index].field)}»`;
    body = body.replaceAll(`{{${name}}}`, value);
  });
  return body;
}
